import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import ErrorBoundary from './components/ErrorBoundary';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './common/ScrollToTop';
import LoadingFallback from './components/LoadingFallback';
import SkipLink from './components/SkipLink';
import A11yAnnouncer from './components/A11yAnnouncer';
import useRouteFocus from './hooks/useRouteFocus';

// Eagerly loaded components (critical for initial render)
import Home from './pages/Home';
import Services from './pages/Services';

// Lazy loaded components (non-critical routes)
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Book = lazy(() => import('./pages/Book'));
const Articles = lazy(() => import('./pages/Articles'));
const ArticleDetail = lazy(() => import('./pages/ArticleDetail'));
const Process = lazy(() => import('./pages/Process'));
const Disclaimer = lazy(() => import('./pages/Disclaimer'));
const Work = lazy(() => import('./pages/Work'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Cookies = lazy(() => import('./pages/Cookies'));
const NotFound = lazy(() => import('./pages/NotFound'));
const CategoryArticles = lazy(() => import('./pages/CategoryArticles'));
const Resources = lazy(() => import('./pages/Resources'));
const FAQ = lazy(() => import('./pages/FAQ'));
const CookieConsent = lazy(() => import('./components/CookieConsent'));

// Service Pages (lazy loaded)
const StrategyAdvisory = lazy(() => import('./pages/services/StrategyAdvisory'));
const AIAutomation = lazy(() => import('./pages/services/AIAutomation'));
const VideoMedia = lazy(() => import('./pages/services/VideoMedia'));
const CreativeConsulting = lazy(() => import('./pages/services/CreativeConsulting'));
const DigitalProducts = lazy(() => import('./pages/services/DigitalProducts'));
const CryptoEducation = lazy(() => import('./pages/services/CryptoEducation'));
const CryptoConsulting = lazy(() => import('./pages/services/CryptoConsulting'));
const InvestmentsVentures = lazy(() => import('./pages/services/InvestmentsVentures'));
const PropertyManagement = lazy(() => import('./pages/services/PropertyManagement'));
const CommunityEvents = lazy(() => import('./pages/services/CommunityEvents'));
const SpecialProjects = lazy(() => import('./pages/services/SpecialProjects'));

import './App.css';

// RouteFocus wrapper component to use the hook inside Router context
const RouteFocusHandler = () => {
  useRouteFocus();
  return null;
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LanguageProvider>
          <Router>
            <ScrollToTop />
            <RouteFocusHandler />
            <SkipLink />
            <A11yAnnouncer />
            
            <div className="min-h-screen bg-[#0b1112] flex flex-col font-sans">
              <Header />
              <main 
                id="main-content" 
                className="studio-page flex-grow outline-none"
                tabIndex="-1"
              >
                <ErrorBoundary>
                  <Suspense fallback={<LoadingFallback />}>
                    <Routes>
                      {/* Eagerly loaded routes */}
                      <Route path="/" element={<Home />} />
                      <Route path="/services" element={<Services />} />

                      {/* Lazy loaded service routes */}
                      <Route path="/services/strategy-advisory" element={<StrategyAdvisory />} />
                      <Route path="/services/ai-automation" element={<AIAutomation />} />
                      <Route path="/services/video-media" element={<VideoMedia />} />
                      <Route path="/services/creative-consulting" element={<CreativeConsulting />} />
                      <Route path="/services/digital-products" element={<DigitalProducts />} />
                      <Route path="/services/crypto-education" element={<CryptoEducation />} />
                      <Route path="/services/crypto-consulting" element={<CryptoConsulting />} />
                      <Route path="/services/investments-ventures" element={<InvestmentsVentures />} />
                      <Route path="/services/property-management" element={<PropertyManagement />} />
                      <Route path="/services/community-events" element={<CommunityEvents />} />
                      <Route path="/services/special-projects" element={<SpecialProjects />} />

                      {/* Lazy loaded article routes */}
                      <Route path="/articles" element={<Articles />} />
                      <Route path="/articles/category/:slug" element={<CategoryArticles />} />
                      <Route path="/articles/read/:slug" element={<ArticleDetail />} />

                      {/* Lazy loaded other routes */}
                      <Route path="/about" element={<About />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/book" element={<Book />} />
                      <Route path="/process" element={<Process />} />
                      <Route path="/work" element={<Work />} />
                      <Route path="/resources" element={<Resources />} />
                      <Route path="/faq" element={<FAQ />} />
                      <Route path="/disclaimer" element={<Disclaimer />} />
                      <Route path="/privacy" element={<Privacy />} />
                      <Route path="/terms" element={<Terms />} />
                      <Route path="/cookies" element={<Cookies />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
              </main>
              <Footer />
              <Suspense fallback={null}>
                <CookieConsent />
              </Suspense>
            </div>
          </Router>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
